import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CalendarBlank, Clock, Sun, CloudRain, Plus, Trash, MapPin, Wrench, FacebookLogo, WhatsappLogo, CaretLeft, CaretRight, Lightbulb, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const CITIES = ["Mamoudzou","Petite-Terre","Koungou","Majicavo","Sada","Combani","Dzoumogné","Bandrélé"];
const TYPES = ["Toiture","Climatisation","Portail","Peinture","Maçonnerie","Carrelage","Plomberie","Électricité","Rénovation"];

export default function CalendarPanel() {
  const [suggestions, setSuggestions] = useState(null);
  const [events, setEvents] = useState({ posts: [], scheduled: [] });
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', city: 'Mamoudzou', construction_type: 'Toiture', platform: 'facebook', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [sugRes, evRes] = await Promise.all([
        axios.get(`${API}/calendar/suggestions`, { withCredentials: true }),
        axios.get(`${API}/calendar/events`, { withCredentials: true })
      ]);
      setSuggestions(sugRes.data);
      setEvents(evRes.data);
    } catch (error) { console.error("Calendar fetch error:", error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return { posts: [], scheduled: [] };
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayPosts = events.posts.filter(p => p.created_at && p.created_at.startsWith(dateStr));
    const dayScheduled = events.scheduled.filter(s => s.scheduled_date && s.scheduled_date.startsWith(dateStr));
    return { posts: dayPosts, scheduled: dayScheduled };
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setShowModal(true);
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Ajoutez un titre'); return; }
    try {
      await axios.post(`${API}/calendar/schedule`, { ...form, scheduled_date: selectedDate }, { withCredentials: true });
      toast.success('Post planifié !');
      setShowModal(false);
      setForm({ title: '', description: '', city: 'Mamoudzou', construction_type: 'Toiture', platform: 'facebook', notes: '' });
      fetchData();
    } catch { toast.error('Erreur de planification'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/calendar/schedule/${id}`, { withCredentials: true }); fetchData(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  const today = new Date();
  const isToday = (day) => day && today.getFullYear() === currentMonth.getFullYear() && today.getMonth() === currentMonth.getMonth() && today.getDate() === day;
  const days = getDaysInMonth(currentMonth);

  if (loading) return (
    <div className="flex items-center justify-center py-16" data-testid="calendar-loading">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="calendar-panel">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-[#27272A] rounded-lg text-[#A1A1AA] transition-colors" data-testid="prev-month">
              <CaretLeft size={18} weight="bold" />
            </button>
            <h3 className="font-bold text-base text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {MONTHS_FR[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button onClick={nextMonth} className="p-2 hover:bg-[#27272A] rounded-lg text-[#A1A1AA] transition-colors" data-testid="next-month">
              <CaretRight size={18} weight="bold" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS_FR.map(d => (
              <div key={d} className="text-center text-[10px] font-bold text-[#71717A] uppercase tracking-wider py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, i) => {
              const dayEvents = getEventsForDay(day);
              const hasPost = dayEvents.posts.length > 0;
              const hasScheduled = dayEvents.scheduled.length > 0;
              return (
                <div key={i}
                  onClick={() => handleDayClick(day)}
                  className={`relative h-14 sm:h-16 rounded-md text-center flex flex-col items-center justify-start pt-1 transition-all cursor-pointer
                    ${!day ? 'opacity-0' : ''}
                    ${isToday(day) ? 'bg-[#8B5CF6]/20 border border-[#8B5CF6]/50' : 'hover:bg-[#27272A] border border-transparent'}
                  `}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <span className={`text-xs font-medium ${isToday(day) ? 'text-[#A78BFA] font-bold' : 'text-[#E4E4E7]'}`}>{day}</span>
                      <div className="flex gap-0.5 mt-1">
                        {hasPost && <div className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6]" title="Post publié" />}
                        {hasScheduled && <div className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" title="Post planifié" />}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4 mt-3 text-xs text-[#71717A]">
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#8B5CF6]" /> Publié</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#FFB800]" /> Planifié</span>
          </div>
        </div>

        {/* Suggestions sidebar */}
        <div className="space-y-4">
          {/* Seasonal tip */}
          {suggestions?.seasonal && (
            <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                {suggestions.seasonal.season.includes('pluie') ? (
                  <CloudRain size={18} weight="duotone" className="text-blue-400" />
                ) : (
                  <Sun size={18} weight="duotone" className="text-[#FFB800]" />
                )}
                <span className="text-xs font-bold text-[#A78BFA] uppercase tracking-wider">{suggestions.seasonal.season}</span>
              </div>
              <p className="text-sm text-[#E4E4E7] leading-relaxed">{suggestions.seasonal.tip}</p>
            </div>
          )}

          {/* Best times */}
          <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={18} weight="duotone" className="text-[#A78BFA]" />
              <span className="text-sm font-bold text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Meilleurs moments</span>
            </div>
            <div className="space-y-2">
              {suggestions?.best_times?.map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="font-bold text-[#A78BFA] w-12 shrink-0">{t.day.slice(0, 3)}</span>
                  <span className="text-[#FFB800] shrink-0">{t.time}</span>
                  <span className="text-[#71717A] hidden sm:block">{t.tip}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Frequency */}
          <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb size={16} weight="duotone" className="text-[#A78BFA]" />
              <span className="text-xs font-bold text-[#A78BFA]">Conseil</span>
            </div>
            <p className="text-sm text-[#E4E4E7]">{suggestions?.recommended_frequency}</p>
          </div>
        </div>
      </div>

      {/* Upcoming scheduled */}
      {events.scheduled.length > 0 && (
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <CalendarBlank size={16} weight="duotone" className="inline mr-1.5 text-[#FFB800]" />
            Posts planifiés
          </h3>
          <div className="space-y-2">
            {events.scheduled.map(s => (
              <div key={s.id} className="flex items-center justify-between px-3 py-2.5 bg-[#09090B] rounded-lg border border-[#27272A]" data-testid={`scheduled-${s.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-bold text-[#FFB800] bg-[#FFB800]/10 px-2 py-0.5 rounded">{new Date(s.scheduled_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                  <span className="text-sm text-[#E4E4E7] font-medium">{s.title}</span>
                  <span className="flex items-center gap-1 text-xs text-[#71717A]"><MapPin size={10} />{s.city}</span>
                  <span className="flex items-center gap-1 text-xs text-[#71717A]"><Wrench size={10} />{s.construction_type}</span>
                  {s.platform === 'facebook' ? <FacebookLogo size={12} className="text-blue-400" /> : <WhatsappLogo size={12} className="text-green-400" />}
                </div>
                <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-scheduled-${s.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="schedule-modal">
          <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-6 w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Planifier un post
              </h3>
              <button onClick={() => setShowModal(false)} className="text-[#71717A] hover:text-[#F4F4F5] p-1">
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-[#A78BFA] mb-4">
              <CalendarBlank size={12} className="inline mr-1" weight="duotone" />
              {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <form onSubmit={handleSchedule} className="space-y-3">
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Titre du post (ex: Toiture à Sada)"
                className="w-full h-11 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none placeholder:text-[#71717A]"
                data-testid="schedule-title" required />
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description (optionnel)" rows={2}
                className="w-full p-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none resize-none placeholder:text-[#71717A]"
                data-testid="schedule-description" />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                  className="h-11 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none" data-testid="schedule-city">
                  {CITIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <select value={form.construction_type} onChange={e => setForm({...form, construction_type: e.target.value})}
                  className="h-11 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none" data-testid="schedule-type">
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({...form, platform: 'facebook'})}
                  className={`flex-1 h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 ${form.platform === 'facebook' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A]'}`}>
                  <FacebookLogo size={16} /> Facebook
                </button>
                <button type="button" onClick={() => setForm({...form, platform: 'whatsapp'})}
                  className={`flex-1 h-10 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 ${form.platform === 'whatsapp' ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-[#09090B] text-[#A1A1AA] border-[#27272A]'}`}>
                  <WhatsappLogo size={16} /> WhatsApp
                </button>
              </div>
              <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notes personnelles (optionnel)"
                className="w-full h-11 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] text-sm focus:border-[#8B5CF6] outline-none placeholder:text-[#71717A]"
                data-testid="schedule-notes" />
              <button type="submit" data-testid="schedule-submit"
                className="w-full h-12 bg-[#8B5CF6] text-white font-bold rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center justify-center gap-2">
                <Plus size={18} weight="bold" /> Planifier
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
